import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Text, Title } from '@tremor/react';
import type { ServiceOrder, User } from '../../api/types';
import { fetchServiceOrders } from '../../api/service';
import { statusKey, type StatusKey } from './Resumen';
import { isOverdue } from './Tablero';
import { ptimeToSlot, weekStartMonday, weekDays, HALF_HOUR_SLOTS } from '../../lib/scheduleTime';
import { localYMD } from '../../lib/format';
import { t } from '../../lib/i18n';
import ScheduleModal, { canSchedule } from './ScheduleModal';

export interface AgendaCard {
  sid: number; tnum: number; anum_name: string; vehicle: string;
  promise_date: string; promise_time: number;
  tech: string; techCode: string; status: StatusKey;
}

/** One card per WO (first job-line row wins for the header fields). */
export function dedupeWos(orders: ServiceOrder[]): AgendaCard[] {
  const byWo = new Map<string, AgendaCard>();
  for (const o of orders) {
    const key = `${o.sid}-${o.tnum}`;
    if (byWo.has(key)) continue;
    byWo.set(key, {
      sid: o.sid, tnum: o.tnum, anum_name: o.anum_name || String(o.anum),
      vehicle: [o.equip_desc, o.equip_plate].filter(Boolean).join(' · '),
      promise_date: o.promise_date, promise_time: o.promise_time,
      tech: o.header_tech_name || o.tech_name || o.tech || '—',
      techCode: o.header_tech || o.tech || '', status: statusKey(o),
    });
  }
  return [...byWo.values()];
}

const SIN_HORA = '';

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(d.getDate() + n);
  return x;
}

/**
 * Agenda — the week calendar. Rows are 30-min time-of-day slots (+ a "Sin hora"
 * band for WOs with no promise time), columns are Mon–Sat of the visible week.
 * Fetches its OWN promise-date window (independent of the cockpit shell's
 * idate-based list), dedupes to one chip per WO, rings overdue chips, and opens
 * a WO on click (Task 6 swaps this for the edit modal).
 */
export default function Agenda({
  user, sid, onOpenWo, initialDate,
}: {
  user: User; sid: number | null; onOpenWo: (sid: number, tnum: number) => void; initialDate?: Date;
}) {
  const [weekStart, setWeekStart] = useState(() => weekStartMonday(initialDate ?? new Date()));
  const [editCard, setEditCard] = useState<AgendaCard | null>(null);
  const [rows, setRows] = useState<ServiceOrder[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const c = t.service.cockpit;

  useEffect(() => {
    let alive = true;
    setRows(null); setErr(null);
    fetchServiceOrders({
      from: localYMD(days[0]), to: localYMD(days[5]), sid, status: 'all', dateField: 'promise',
    })
      .then((r) => { if (alive) setRows(r); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, sid]);

  const cards = useMemo(() => dedupeWos(rows ?? []), [rows]);

  // Technician dropdown options for the edit modal, derived from the fetched set.
  const techList = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of rows ?? []) {
      const code = o.header_tech || o.tech;
      if (code) m.set(code, o.header_tech_name || o.tech_name || code);
    }
    return [...m.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Chip click: open the gated edit modal when the user can write, else jump to 360.
  const openChip = (card: AgendaCard) => {
    if (canSchedule(user)) setEditCard(card);
    else onOpenWo(card.sid, card.tnum);
  };

  // On a successful write, patch this view's own rows in place so the chip moves
  // immediately (the server read-cache reconciles within its ~60s TTL).
  const applyPatch = (data: { sid: number; tnum: number; status: string; pdate: string; ptime: number; tech: string }) => {
    const name = techList.find((tt) => tt.code === data.tech)?.name ?? data.tech;
    setRows((prev) => (prev ?? []).map((o) =>
      o.sid === data.sid && o.tnum === data.tnum
        ? { ...o, promise_date: data.pdate, promise_time: data.ptime, wo_status: data.status, header_tech: data.tech, header_tech_name: name }
        : o));
    setEditCard(null);
  };

  const today = new Date();
  const slots = useMemo(() => [SIN_HORA, ...HALF_HOUR_SLOTS], []);

  // grid[slotLabel] -> Map<ymd, AgendaCard[]>
  const grid = useMemo(() => {
    const g = new Map<string, Map<string, AgendaCard[]>>();
    for (const s of slots) g.set(s, new Map());
    for (const card of cards) {
      const cell = g.get(ptimeToSlot(card.promise_time)) ?? g.get(SIN_HORA)!;
      const arr = cell.get(card.promise_date) ?? [];
      arr.push(card);
      cell.set(card.promise_date, arr);
    }
    return g;
  }, [cards, slots]);

  const fmtDay = (d: Date) => d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' });

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <Title className="text-base">
          {days[0].toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – {days[5].toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
        </Title>
        <div className="flex gap-2">
          <Button size="xs" variant="secondary" onClick={() => setWeekStart(weekStartMonday(addDays(weekStart, -7)))}>‹</Button>
          <Button size="xs" variant="secondary" onClick={() => setWeekStart(weekStartMonday(new Date()))}>{c.agenda.today}</Button>
          <Button size="xs" variant="secondary" onClick={() => setWeekStart(weekStartMonday(addDays(weekStart, 7)))}>›</Button>
        </div>
      </div>

      {err && <Text className="text-red-500">{err}</Text>}
      {!err && rows === null && <Text>…</Text>}
      {!err && rows !== null && cards.length === 0 && <Text>{c.agenda.empty}</Text>}

      {!err && rows !== null && cards.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left p-1 w-16 text-tremor-content"></th>
                {days.map((d) => (
                  <th key={localYMD(d)} className="text-left p-1 text-tremor-content-strong capitalize">{fmtDay(d)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((slot) => {
                const cell = grid.get(slot)!;
                const rowHasAny = days.some((d) => (cell.get(localYMD(d)) ?? []).length > 0);
                if (!rowHasAny) return null;
                return (
                  <tr key={slot || 'sinhora'} className="align-top border-t border-tremor-border">
                    <td className="p-1 text-tremor-content tabular-nums whitespace-nowrap">{slot || c.agenda.sinHora}</td>
                    {days.map((d) => {
                      const ymd = localYMD(d);
                      const list = cell.get(ymd) ?? [];
                      return (
                        <td key={ymd} className="p-1">
                          <div className="flex flex-col gap-1">
                            {list.map((card) => {
                              const over = isOverdue(card, today);
                              return (
                                <button
                                  key={`${card.sid}-${card.tnum}`}
                                  onClick={() => openChip(card)}
                                  className={`text-left rounded px-1.5 py-1 bg-tremor-background-muted hover:bg-tremor-background-subtle ${over ? 'ring-2 ring-red-500' : ''}`}
                                >
                                  <div className="font-medium">#{card.tnum}</div>
                                  <div className="text-tremor-content truncate max-w-[10rem]">{card.vehicle || card.anum_name}</div>
                                  <div className="flex items-center gap-1">
                                    <Text className="text-[10px]">{card.tech}</Text>
                                    {over && <Badge color="red" size="xs">{c.overdue}</Badge>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editCard && (
        <ScheduleModal
          user={user} card={editCard} techs={techList}
          onSaved={applyPatch} onClose={() => setEditCard(null)} onView360={onOpenWo}
        />
      )}
    </Card>
  );
}
